import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../api/client';
import {
  Upload, FileSpreadsheet, ArrowRight, ArrowLeft, CheckCircle2,
  AlertCircle, Download, X, Loader2, TableProperties,
} from 'lucide-react';

// ── Field definitions for each data type ──────────────────
const ENTITY_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Entity Name', required: true },
  { key: 'entityType', label: 'Entity Type', required: true, hint: 'e.g., family_llc, irrevocable_trust, s_corp' },
  { key: 'ein', label: 'EIN' },
  { key: 'formationDate', label: 'Formation Date' },
  { key: 'domicileState', label: 'Domicile State', hint: '2-letter code' },
  { key: 'isGrantorTrust', label: 'Grantor Trust', hint: 'TRUE/FALSE' },
  { key: 'isOperatingBusiness', label: 'Operating Business', hint: 'TRUE/FALSE' },
  { key: 'isCloselyHeld', label: 'Closely Held', hint: 'TRUE/FALSE' },
  { key: 'notes', label: 'Notes' },
];

const ASSET_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Asset Name', required: true },
  { key: 'assetType', label: 'Asset Type', required: true, hint: 'e.g., real_estate, private_equity, cash' },
  { key: 'entityName', label: 'Entity Name', hint: 'Links to entity by name' },
  { key: 'currentValue', label: 'Current FMV', required: true, numeric: true },
  { key: 'costBasis', label: 'Cost Basis', numeric: true },
  { key: 'acquisitionDate', label: 'Acquisition Date' },
  { key: 'appreciationRate', label: 'Appreciation Rate', numeric: true, hint: 'Decimal (0.05 = 5%)' },
  { key: 'incomeYield', label: 'Income Yield', numeric: true, hint: 'Decimal (0.06 = 6%)' },
  { key: 'isLiquid', label: 'Liquid', hint: 'TRUE/FALSE' },
  { key: 'stepUpEligible', label: 'Step-Up Eligible', hint: 'TRUE/FALSE' },
  { key: 'propertyType', label: 'Property Type', hint: 'office, multifamily, retail, etc.' },
  { key: 'propertyAddress', label: 'Property Address' },
  { key: 'propertyState', label: 'Property State', hint: '2-letter code' },
  { key: 'noi', label: 'NOI', numeric: true },
  { key: 'capRate', label: 'Cap Rate', numeric: true },
  { key: 'squareFootage', label: 'Square Footage', numeric: true },
  { key: 'unitCount', label: 'Unit Count', numeric: true },
  { key: 'targetReturnMultiple', label: 'Target Multiple', numeric: true },
  { key: 'investmentHorizonYears', label: 'Horizon (years)', numeric: true },
  { key: 'notes', label: 'Notes' },
];

const LIABILITY_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Instrument Name', required: true },
  { key: 'liabilityType', label: 'Type', required: true, hint: 'e.g., mortgage, credit_line, term_loan' },
  { key: 'entityName', label: 'Entity Name', hint: 'Links to entity by name' },
  { key: 'outstandingBalance', label: 'Outstanding Balance', required: true, numeric: true },
  { key: 'creditLimit', label: 'Credit Limit', numeric: true },
  { key: 'interestRate', label: 'Interest Rate', numeric: true, hint: 'Decimal (0.0575 = 5.75%)' },
  { key: 'isVariableRate', label: 'Variable Rate', hint: 'TRUE/FALSE' },
  { key: 'maturityDate', label: 'Maturity Date' },
  { key: 'amortizationType', label: 'Amortization', hint: 'io, amortizing, balloon' },
  { key: 'paymentFrequency', label: 'Payment Frequency', hint: 'monthly, quarterly, annual' },
  { key: 'annualDebtService', label: 'Annual Debt Service', numeric: true },
  { key: 'prepaymentPenaltyPct', label: 'Prepayment Penalty %', numeric: true },
  { key: 'notes', label: 'Notes' },
];

const PARTNER_FIELDS: FieldDef[] = [
  { key: 'entityName', label: 'Entity Name', required: true, hint: 'Links partner to entity by name' },
  { key: 'memberFirstName', label: 'First Name', required: true },
  { key: 'memberLastName', label: 'Last Name', required: true },
  { key: 'role', label: 'Role', hint: 'GP, LP, Member, Beneficiary' },
  { key: 'ownershipPct', label: 'Ownership %', required: true, numeric: true, hint: 'e.g., 35 for 35%' },
  { key: 'capitalAccountBalance', label: 'Capital Account', numeric: true },
  { key: 'estimatedMarginalRate', label: 'Marginal Tax Rate', numeric: true, hint: 'Decimal (0.37 = 37%)' },
  { key: 'notes', label: 'Notes' },
];

const CASH_FLOW_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Description', required: true },
  { key: 'cashFlowType', label: 'Type', required: true, hint: 'income, expense, distribution, contribution, tax_payment' },
  { key: 'entityName', label: 'Entity Name', hint: 'Links to entity by name' },
  { key: 'memberFirstName', label: 'Member First Name' },
  { key: 'memberLastName', label: 'Member Last Name' },
  { key: 'amount', label: 'Amount', required: true, numeric: true },
  { key: 'frequency', label: 'Frequency', hint: 'one_time, monthly, quarterly, annual' },
  { key: 'startDate', label: 'Start Date' },
  { key: 'endDate', label: 'End Date' },
  { key: 'growthRate', label: 'Growth Rate', numeric: true, hint: 'Decimal (0.03 = 3%)' },
  { key: 'isTaxable', label: 'Taxable', hint: 'TRUE/FALSE' },
  { key: 'taxCategory', label: 'Tax Category', hint: 'ordinary, capital_gain, tax_exempt' },
  { key: 'source', label: 'Source', hint: 'e.g., osprey_pe' },
  { key: 'notes', label: 'Notes' },
];

const DATA_TYPES = [
  { key: 'entities', label: 'Entities', fields: ENTITY_FIELDS },
  { key: 'assets', label: 'Assets', fields: ASSET_FIELDS },
  { key: 'liabilities', label: 'Liabilities', fields: LIABILITY_FIELDS },
  { key: 'partners', label: 'Partners / Ownership', fields: PARTNER_FIELDS },
  { key: 'cashFlows', label: 'Cash Flows', fields: CASH_FLOW_FIELDS },
] as const;

type DataTypeKey = 'entities' | 'assets' | 'liabilities' | 'partners' | 'cashFlows';

interface FieldDef {
  key: string;
  label: string;
  required?: boolean;
  numeric?: boolean;
  hint?: string;
}

interface SheetMapping {
  sheetName: string;
  dataType: DataTypeKey | '';
  headers: string[];
  columnMap: Record<string, string>; // fieldKey -> headerName
  preview: any[];
  rowCount: number;
}

// ── Auto-detect column mappings ───────────────────────────
const HEADER_ALIASES: Record<string, string[]> = {
  name: ['name', 'entity name', 'asset name', 'instrument name', 'instrument', 'description'],
  entityType: ['entity type', 'type', 'entity_type', 'structure'],
  assetType: ['asset type', 'type', 'asset_type', 'class', 'asset class'],
  liabilityType: ['liability type', 'type', 'liability_type', 'loan type', 'debt type'],
  ein: ['ein', 'tax id', 'fein'],
  formationDate: ['formation date', 'formed', 'date formed', 'formation_date'],
  domicileState: ['domicile state', 'state', 'domicile', 'domicile_state'],
  entityName: ['entity', 'entity name', 'held in', 'entity_name', 'holding entity'],
  currentValue: ['current value', 'fmv', 'fair market value', 'value', 'current_value', 'market value'],
  costBasis: ['cost basis', 'basis', 'cost_basis', 'original cost'],
  acquisitionDate: ['acquisition date', 'acquired', 'purchase date', 'acquisition_date'],
  appreciationRate: ['appreciation rate', 'growth rate', 'appreciation', 'appreciation_rate'],
  incomeYield: ['income yield', 'yield', 'income_yield', 'distribution rate'],
  isLiquid: ['liquid', 'is liquid', 'is_liquid', 'liquidity'],
  stepUpEligible: ['step up eligible', 'step_up_eligible', 'step up', 'basis step up'],
  outstandingBalance: ['outstanding balance', 'balance', 'outstanding_balance', 'principal', 'loan balance'],
  creditLimit: ['credit limit', 'limit', 'credit_limit', 'facility size'],
  interestRate: ['interest rate', 'rate', 'interest_rate', 'coupon'],
  isVariableRate: ['variable rate', 'is variable', 'is_variable_rate', 'floating'],
  maturityDate: ['maturity date', 'maturity', 'maturity_date', 'matures'],
  isGrantorTrust: ['grantor trust', 'is grantor', 'is_grantor_trust', 'grantor'],
  isOperatingBusiness: ['operating business', 'is operating', 'is_operating_business', 'operating'],
  isCloselyHeld: ['closely held', 'is closely held', 'is_closely_held'],
  propertyType: ['property type', 'property_type', 'prop type'],
  propertyAddress: ['property address', 'address', 'property_address'],
  propertyState: ['property state', 'situs state', 'property_state', 'situs'],
  noi: ['noi', 'net operating income'],
  capRate: ['cap rate', 'cap_rate', 'capitalization rate'],
  squareFootage: ['square footage', 'sq ft', 'sqft', 'square_footage', 'sf'],
  unitCount: ['unit count', 'units', 'unit_count', 'num units'],
  amortizationType: ['amortization type', 'amortization', 'amort type', 'amortization_type'],
  paymentFrequency: ['payment frequency', 'frequency', 'payment_frequency', 'pay frequency'],
  annualDebtService: ['annual debt service', 'debt service', 'annual_debt_service', 'ads'],
  targetReturnMultiple: ['target multiple', 'return multiple', 'moic', 'target_return_multiple'],
  investmentHorizonYears: ['investment horizon', 'horizon', 'investment_horizon_years', 'hold period'],
  notes: ['notes', 'comments', 'memo', 'description'],
  // Partner fields
  memberFirstName: ['first name', 'first_name', 'firstname', 'partner first name', 'member first name'],
  memberLastName: ['last name', 'last_name', 'lastname', 'partner last name', 'member last name'],
  role: ['role', 'partner role', 'member role', 'type', 'partner type'],
  ownershipPct: ['ownership %', 'ownership_pct', 'ownership pct', 'ownership', 'pct', '% owned', 'interest'],
  capitalAccountBalance: ['capital account', 'capital_account_balance', 'capital balance', 'cap account', 'ending balance'],
  estimatedMarginalRate: ['marginal rate', 'estimated_marginal_rate', 'tax rate', 'marginal tax rate'],
  // Cash flow fields
  cashFlowType: ['cash flow type', 'type', 'cash_flow_type', 'flow type', 'category'],
  amount: ['amount', 'annual amount', 'value', 'cash flow amount'],
  frequency: ['frequency', 'period', 'recurrence'],
  startDate: ['start date', 'start_date', 'effective date', 'begin date'],
  endDate: ['end date', 'end_date', 'termination date', 'expiry'],
  growthRate: ['growth rate', 'growth_rate', 'escalation', 'annual increase'],
  isTaxable: ['taxable', 'is_taxable', 'is taxable'],
  taxCategory: ['tax category', 'tax_category', 'income type', 'k1 category'],
  source: ['source', 'data source', 'origin', 'imported from'],
};

function autoDetectMapping(headers: string[], fields: FieldDef[]): Record<string, string> {
  const map: Record<string, string> = {};
  const normalizedHeaders = headers.map(h => h.toLowerCase().trim());

  for (const field of fields) {
    const aliases = HEADER_ALIASES[field.key] || [field.key.toLowerCase()];
    for (const alias of aliases) {
      const idx = normalizedHeaders.indexOf(alias);
      if (idx !== -1) {
        map[field.key] = headers[idx];
        break;
      }
    }
  }

  return map;
}

// ── Template Generation ───────────────────────────────────
function generateTemplate(type: 'master' | 'pe_bridge' | DataTypeKey) {
  const wb = XLSX.utils.book_new();

  if (type === 'master' || type === 'entities') {
    const data = [ENTITY_FIELDS.map(f => f.label)];
    data.push(['Smith Family Holdings, LP', 'family_lp', '12-3456789', '2020-01-15', 'OK', 'FALSE', 'TRUE', 'TRUE', 'Main holding entity']);
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = ENTITY_FIELDS.map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Entities');
  }

  if (type === 'master' || type === 'assets') {
    const data = [ASSET_FIELDS.map(f => f.label)];
    data.push(['Office Complex — Hartford', 'real_estate', 'Smith Real Estate, LLC', '4200000', '2800000', '2018-06-01', '0.05', '0.062', 'FALSE', 'TRUE', 'office', '123 Main St, Hartford, CT', 'CT', '260400', '0.062', '45000', '', '', '', 'Class A office']);
    data.push(['PE Fund III', 'private_equity', 'Smith Family Holdings, LP', '3000000', '2000000', '2022-03-15', '0.12', '0', 'FALSE', 'TRUE', '', '', '', '', '', '', '', '2.5', '7', 'Vintage 2022']);
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = ASSET_FIELDS.map(() => ({ wch: 18 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Assets');
  }

  if (type === 'master' || type === 'liabilities') {
    const data = [LIABILITY_FIELDS.map(f => f.label)];
    data.push(['First Lien — Office Complex', 'mortgage', 'Smith Real Estate, LLC', '8200000', '', '0.0575', 'FALSE', '2029-03-15', 'amortizing', 'monthly', '580000', '3', 'Secured by Hartford office']);
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = LIABILITY_FIELDS.map(() => ({ wch: 18 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Liabilities');
  }

  if (type === 'master' || type === 'partners') {
    const data = [PARTNER_FIELDS.map(f => f.label)];
    data.push(['Smith Family Holdings, LP', 'John', 'Smith', 'GP', '35', '4200000', '0.37', 'Managing partner']);
    data.push(['Smith Family Holdings, LP', 'Jane', 'Smith', 'LP', '35', '3800000', '0.37', '']);
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = PARTNER_FIELDS.map(() => ({ wch: 18 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Partners');
  }

  if (type === 'master' || type === 'cashFlows') {
    const data = [CASH_FLOW_FIELDS.map(f => f.label)];
    data.push(['RE Rental Income', 'income', 'Smith Real Estate, LLC', '', '', '260400', 'annual', '2024-01-01', '', '0.03', 'TRUE', 'ordinary', '', 'Net operating income']);
    data.push(['Management Fee', 'expense', 'Smith Family Holdings, LP', '', '', '120000', 'annual', '2024-01-01', '', '0.02', 'FALSE', '', '', 'Advisory + admin']);
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = CASH_FLOW_FIELDS.map(() => ({ wch: 18 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Cash Flows');
  }

  if (type === 'pe_bridge') {
    // PE Bridge: exact format Osprey PE should export
    const entData = [['Entity Name', 'Entity Type', 'EIN', 'Formation Date', 'Domicile State', 'Grantor Trust', 'Operating Business', 'Closely Held', 'Notes']];
    entData.push(['Smith Family Holdings, LP', 'family_lp', '12-3456789', '2020-01-15', 'OK', 'FALSE', 'TRUE', 'TRUE', 'Imported from Osprey PE']);
    const entWs = XLSX.utils.aoa_to_sheet(entData);
    entWs['!cols'] = entData[0].map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, entWs, 'PE Entities');

    const partData = [['Entity Name', 'First Name', 'Last Name', 'Role', 'Ownership %', 'Capital Account', 'Marginal Tax Rate', 'Notes']];
    partData.push(['Smith Family Holdings, LP', 'John', 'Smith', 'GP', '35', '4200000', '0.37', '']);
    partData.push(['Smith Family Holdings, LP', 'Jane', 'Smith', 'LP', '35', '3800000', '0.37', '']);
    partData.push(['Smith Family Holdings, LP', 'Smith Family Trust', '', 'LP', '20', '2100000', '', 'Irrevocable trust']);
    partData.push(['Smith Family Holdings, LP', 'Smith Foundation', '', 'LP', '10', '1050000', '', '501(c)(3)']);
    const partWs = XLSX.utils.aoa_to_sheet(partData);
    partWs['!cols'] = partData[0].map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, partWs, 'PE Partners');

    const cfData = [['Description', 'Type', 'Entity Name', 'Member First Name', 'Member Last Name', 'Amount', 'Frequency', 'Start Date', 'End Date', 'Growth Rate', 'Taxable', 'Tax Category', 'Source', 'Notes']];
    cfData.push(['K-1 Ordinary Income — J. Smith', 'income', 'Smith Family Holdings, LP', 'John', 'Smith', '185000', 'annual', '2024-01-01', '', '0.03', 'TRUE', 'ordinary', 'osprey_pe', 'From 2024 K-1']);
    cfData.push(['Annual Distribution — J. Smith', 'distribution', 'Smith Family Holdings, LP', 'John', 'Smith', '150000', 'annual', '2024-01-01', '', '0', 'FALSE', '', 'osprey_pe', 'Quarterly cash distributions annualized']);
    const cfWs = XLSX.utils.aoa_to_sheet(cfData);
    cfWs['!cols'] = cfData[0].map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, cfWs, 'PE K1 Income');
  }

  const filename = type === 'master' ? 'osprey-legacy-master-template.xlsx'
    : type === 'pe_bridge' ? 'osprey-pe-to-legacy-bridge.xlsx'
    : `osprey-legacy-${type}-template.xlsx`;
  XLSX.writeFile(wb, filename);
}

// ── Transform row using column mapping ────────────────────
function transformRow(row: any, columnMap: Record<string, string>, fields: FieldDef[]): any {
  const result: any = {};
  for (const field of fields) {
    const header = columnMap[field.key];
    if (!header) continue;
    let val = row[header];
    if (val === undefined || val === null || val === '') continue;

    // Type coercion
    if (field.numeric) {
      const n = typeof val === 'string' ? parseFloat(val.replace(/[$,]/g, '')) : Number(val);
      if (!isNaN(n)) result[field.key] = n;
    } else if (field.key.startsWith('is') || field.key === 'stepUpEligible') {
      if (typeof val === 'boolean') result[field.key] = val;
      else if (typeof val === 'string') result[field.key] = ['true', 'yes', '1', 'y'].includes(val.toLowerCase());
    } else {
      result[field.key] = String(val).trim();
    }
  }
  return result;
}

// ── Validate row ──────────────────────────────────────────
function validateRow(row: any, fields: FieldDef[]): string[] {
  const errors: string[] = [];
  for (const field of fields) {
    if (field.required && (row[field.key] === undefined || row[field.key] === null || row[field.key] === '')) {
      errors.push(`Missing required field: ${field.label}`);
    }
  }
  return errors;
}

// ── Main Component ────────────────────────────────────────
export default function ImportWizardPage() {
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState('');
  const [sheets, setSheets] = useState<SheetMapping[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string[][]>>({});
  const [dragOver, setDragOver] = useState(false);

  // ── Step 0: Upload ──────────────────────────────────



  // ── Step 1: Map Sheets ──────────────────────────────
  function setSheetType(idx: number, type: DataTypeKey | '') {
    setSheets(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], dataType: type };
      // Re-auto-detect column mappings
      if (type) {
        const fields = DATA_TYPES.find(d => d.key === type)?.fields || [];
        next[idx].columnMap = autoDetectMapping(next[idx].headers, fields);
      } else {
        next[idx].columnMap = {};
      }
      return next;
    });
  }

  // ── Step 2: Map Columns ─────────────────────────────
  function setColumnMapping(sheetIdx: number, fieldKey: string, headerName: string) {
    setSheets(prev => {
      const next = [...prev];
      next[sheetIdx] = {
        ...next[sheetIdx],
        columnMap: { ...next[sheetIdx].columnMap, [fieldKey]: headerName },
      };
      return next;
    });
  }

  // ── Step 3: Validate ────────────────────────────────

  // ── Step 4: Import ──────────────────────────────────

  // ── We need to store full row data, not just preview ──
  // Let me refactor to store all rows

  const [fullData, setFullData] = useState<Record<string, any[]>>({});

  const handleFileV2 = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const allData: Record<string, any[]> = {};

        const mappings: SheetMapping[] = wb.SheetNames.map((name) => {
          const ws = wb.Sheets[name];
          const json = XLSX.utils.sheet_to_json<any>(ws, { defval: '' });
          allData[name] = json;
          const headers = json.length > 0 ? Object.keys(json[0]) : [];

          let autoType: DataTypeKey | '' = '';
          const lower = name.toLowerCase();
          // Standard sheet name detection
          if (lower.includes('entit') || lower.includes('trust') || lower.includes('structure')) autoType = 'entities';
          else if (lower.includes('asset') || lower.includes('holding') || lower.includes('portfolio')) autoType = 'assets';
          else if (lower.includes('liab') || lower.includes('debt') || lower.includes('loan') || lower.includes('obligation')) autoType = 'liabilities';
          else if (lower.includes('partner') || lower.includes('ownership') || lower.includes('member') || lower.includes('stake')) autoType = 'partners';
          else if (lower.includes('cash flow') || lower.includes('cashflow') || lower.includes('k-1') || lower.includes('k1 income') || lower.includes('distribution')) autoType = 'cashFlows';
          // Osprey PE specific sheet names
          if (lower === 'pe_entities' || lower === 'pe entities') autoType = 'entities';
          else if (lower === 'pe_partners' || lower === 'pe partners' || lower === 'pe_ownership') autoType = 'partners';
          else if (lower === 'pe_k1_income' || lower === 'pe k1 income' || lower === 'pe_cash_flows') autoType = 'cashFlows';

          const fields = autoType ? DATA_TYPES.find(d => d.key === autoType)?.fields || [] : [];
          const columnMap = autoType ? autoDetectMapping(headers, fields) : {};

          return {
            sheetName: name, dataType: autoType, headers, columnMap,
            preview: json.slice(0, 5), rowCount: json.length,
          };
        });

        setFullData(allData);
        setFileName(file.name);
        setSheets(mappings);
        setStep(1);
      } catch (err) {
        alert('Failed to parse file. Please check the format.');
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  // Override handlers to use V2
  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileV2(file);
  }, [handleFileV2]);

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileV2(file);
  }, [handleFileV2]);

  // Real import function
  async function executeImport() {
    setImporting(true);
    try {
      const payload: { entities: any[]; assets: any[]; liabilities: any[]; partners: any[]; cashFlows: any[] } = {
        entities: [], assets: [], liabilities: [], partners: [], cashFlows: [],
      };

      // Process entities first (so assets/liabilities/partners can reference them)
      const entitySheets = sheets.filter(s => s.dataType === 'entities');
      const assetSheets = sheets.filter(s => s.dataType === 'assets');
      const liabilitySheets = sheets.filter(s => s.dataType === 'liabilities');
      const partnerSheets = sheets.filter(s => s.dataType === 'partners');
      const cashFlowSheets = sheets.filter(s => s.dataType === 'cashFlows');

      for (const sheet of entitySheets) {
        const rows = fullData[sheet.sheetName] || [];
        const fields = ENTITY_FIELDS;
        for (const row of rows) {
          const transformed = transformRow(row, sheet.columnMap, fields);
          if (transformed.name) payload.entities.push(transformed);
        }
      }

      for (const sheet of assetSheets) {
        const rows = fullData[sheet.sheetName] || [];
        const fields = ASSET_FIELDS;
        for (const row of rows) {
          const transformed = transformRow(row, sheet.columnMap, fields);
          if (transformed.name && transformed.currentValue !== undefined) payload.assets.push(transformed);
        }
      }

      for (const sheet of liabilitySheets) {
        const rows = fullData[sheet.sheetName] || [];
        const fields = LIABILITY_FIELDS;
        for (const row of rows) {
          const transformed = transformRow(row, sheet.columnMap, fields);
          if (transformed.name && transformed.outstandingBalance !== undefined) payload.liabilities.push(transformed);
        }
      }

      for (const sheet of partnerSheets) {
        const rows = fullData[sheet.sheetName] || [];
        const fields = PARTNER_FIELDS;
        for (const row of rows) {
          const transformed = transformRow(row, sheet.columnMap, fields);
          if (transformed.entityName && transformed.memberFirstName && transformed.ownershipPct !== undefined) {
            payload.partners.push(transformed);
          }
        }
      }

      for (const sheet of cashFlowSheets) {
        const rows = fullData[sheet.sheetName] || [];
        const fields = CASH_FLOW_FIELDS;
        for (const row of rows) {
          const transformed = transformRow(row, sheet.columnMap, fields);
          if (transformed.name && transformed.amount !== undefined) {
            // Tag PE imports
            if (sheet.sheetName.toLowerCase().includes('pe_') || sheet.sheetName.toLowerCase().includes('pe ')) {
              transformed.source = 'osprey_pe';
            }
            payload.cashFlows.push(transformed);
          }
        }
      }

      const res = await api.post('/import/bulk', payload);
      setImportResult(res.data);
      setStep(4);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  // Validate with full data
  function runFullValidation() {
    const errors: Record<string, string[][]> = {};
    

    for (const sheet of sheets) {
      if (!sheet.dataType) continue;
      const fields = DATA_TYPES.find(d => d.key === sheet.dataType)?.fields || [];
      const rows = fullData[sheet.sheetName] || [];
      const sheetErrors: string[][] = [];

      for (const row of rows) {
        const transformed = transformRow(row, sheet.columnMap, fields);
        const rowErrors = validateRow(transformed, fields);
        sheetErrors.push(rowErrors);
        
      }
      errors[sheet.sheetName] = sheetErrors;
    }

    setValidationErrors(errors);
    setStep(3);
  }

  // Count mapped sheets
  const mappedSheets = sheets.filter(s => s.dataType !== '');
  const totalRows = mappedSheets.reduce((sum, s) => sum + s.rowCount, 0);

  const hasValidationErrors = Object.values(validationErrors).some(
    sheetErrs => sheetErrs.some(rowErrs => rowErrs.length > 0)
  );

  // ── Render ──────────────────────────────────────────
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-navy-900">Data Import Center</h1>
          <p className="text-sm text-navy-400 font-sans mt-1">
            Upload existing workbooks or download templates to populate your planning model
          </p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-0 mb-8">
        {['Upload', 'Map Sheets', 'Map Columns', 'Validate', 'Complete'].map((label, i) => (
          <div key={i} className="flex items-center">
            <div
              className={`flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors ${
                step === i ? 'bg-navy-700 text-white' :
                step > i ? 'bg-hunter-500 text-white' : 'bg-ivory-200 text-navy-400'
              }`}
              onClick={() => { if (i < step) setStep(i); }}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-sans font-bold ${
                step > i ? 'bg-white/20' : ''
              }`}>
                {step > i ? '✓' : i + 1}
              </div>
              <span className="text-sm font-sans font-medium">{label}</span>
            </div>
            {i < 4 && <div className="w-8 h-0.5 bg-ivory-300" />}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2">

          {/* ── Step 0: Upload ──────────────────────────── */}
          {step === 0 && (
            <div className="card">
              <h2 className="card-header">Upload Workbook</h2>
              <div
                onDrop={onDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-navy-500 bg-navy-50' : 'border-ivory-400 bg-ivory-100 hover:border-navy-300'
                }`}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <Upload size={36} className="mx-auto mb-3 text-navy-300" />
                <p className="text-sm font-sans font-semibold text-navy-700 mb-1">
                  Drop your Excel workbook here
                </p>
                <p className="text-xs text-navy-400 font-sans">
                  Supports .xlsx and .csv files
                </p>
                <input
                  id="file-input"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={onInputChange}
                  className="hidden"
                />
              </div>
              <div className="mt-4 p-4 bg-ivory-100 rounded-lg border border-ivory-300">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="text-navy-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-navy-500 font-sans leading-relaxed">
                    The import wizard reads your sheet names and column headers, then guides you through mapping them to the planning model. Common formats are auto-detected. Use our templates below for the best results.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 1: Map Sheets ─────────────────────── */}
          {step === 1 && (
            <div className="card">
              <h2 className="card-header">Map Sheets to Data Types</h2>
              <p className="text-sm text-navy-400 font-sans mb-4">
                We found {sheets.length} sheet{sheets.length !== 1 ? 's' : ''} in <span className="font-semibold text-navy-600">{fileName}</span>. Tell us what each sheet contains.
              </p>
              <div className="space-y-3">
                {sheets.map((sheet, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-4 bg-ivory-100 rounded-lg border border-ivory-300">
                    <FileSpreadsheet size={20} className="text-navy-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-sans font-semibold text-navy-800">{sheet.sheetName}</p>
                      <p className="text-xs text-navy-400 font-sans">{sheet.rowCount} rows · {sheet.headers.length} columns</p>
                    </div>
                    <select
                      value={sheet.dataType}
                      onChange={(e) => setSheetType(idx, e.target.value as DataTypeKey | '')}
                      className="input w-48"
                    >
                      <option value="">— Skip this sheet —</option>
                      <option value="entities">Entities</option>
                      <option value="assets">Assets</option>
                      <option value="liabilities">Liabilities</option>
                      <option value="partners">Partners / Ownership</option>
                      <option value="cashFlows">Cash Flows</option>
                    </select>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-6">
                <button onClick={() => { setStep(0); setSheets([]); setFileName(''); }} className="btn-secondary">
                  <ArrowLeft size={16} className="mr-2" />Back
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={mappedSheets.length === 0}
                  className="btn-primary"
                >
                  Map Columns<ArrowRight size={16} className="ml-2" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Map Columns ────────────────────── */}
          {step === 2 && (
            <div className="card">
              <h2 className="card-header">Map Columns</h2>
              <p className="text-sm text-navy-400 font-sans mb-4">
                Match your spreadsheet columns to our data fields. We've auto-detected what we can.
              </p>
              {mappedSheets.map((sheet, sheetIdx) => {
                const realIdx = sheets.indexOf(sheet);
                const fields = DATA_TYPES.find(d => d.key === sheet.dataType)?.fields || [];
                const mappedCount = Object.values(sheet.columnMap).filter(Boolean).length;

                return (
                  <div key={sheetIdx} className="mb-6 last:mb-0">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-ivory-300">
                      <TableProperties size={16} className="text-navy-500" />
                      <span className="text-sm font-sans font-bold text-navy-700">{sheet.sheetName}</span>
                      <span className="badge-navy">{sheet.dataType}</span>
                      <span className="text-xs text-navy-400 ml-auto">{mappedCount}/{fields.length} mapped</span>
                    </div>
                    <div className="space-y-2">
                      {fields.map((field) => (
                        <div key={field.key} className="flex items-center gap-3">
                          <div className="w-44 flex-shrink-0">
                            <span className={`text-xs font-sans ${field.required ? 'font-bold text-navy-700' : 'text-navy-500'}`}>
                              {field.label}{field.required && ' *'}
                            </span>
                            {field.hint && <p className="text-[10px] text-navy-400">{field.hint}</p>}
                          </div>
                          <ArrowLeft size={14} className="text-navy-300 flex-shrink-0" />
                          <select
                            value={sheet.columnMap[field.key] || ''}
                            onChange={(e) => setColumnMapping(realIdx, field.key, e.target.value)}
                            className="input flex-1"
                          >
                            <option value="">— Not mapped —</option>
                            {sheet.headers.map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-between mt-6">
                <button onClick={() => setStep(1)} className="btn-secondary">
                  <ArrowLeft size={16} className="mr-2" />Back
                </button>
                <button onClick={runFullValidation} className="btn-primary">
                  Validate<ArrowRight size={16} className="ml-2" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Validate ───────────────────────── */}
          {step === 3 && (
            <div className="card">
              <h2 className="card-header">Validation Results</h2>
              {mappedSheets.map((sheet) => {
                const errs = validationErrors[sheet.sheetName] || [];
                const errorRows = errs.filter(r => r.length > 0);
                const validRows = errs.length - errorRows.length;

                return (
                  <div key={sheet.sheetName} className="mb-4 last:mb-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-sans font-bold text-navy-700">{sheet.sheetName}</span>
                      <span className="badge-navy">{sheet.dataType}</span>
                      {errorRows.length === 0 ? (
                        <span className="badge-hunter ml-auto">
                          <CheckCircle2 size={12} className="mr-1" />{validRows} rows valid
                        </span>
                      ) : (
                        <span className="badge-burgundy ml-auto">
                          {errorRows.length} rows with errors
                        </span>
                      )}
                    </div>
                    {errorRows.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 font-sans max-h-32 overflow-y-auto">
                        {errs.map((rowErr, i) =>
                          rowErr.length > 0 ? (
                            <p key={i}>Row {i + 1}: {rowErr.join(', ')}</p>
                          ) : null
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="mt-4 p-4 bg-ivory-100 rounded-lg border border-ivory-300">
                <div className="text-sm font-sans text-navy-600">
                  Ready to import <strong>{totalRows} rows</strong> across {mappedSheets.length} sheet{mappedSheets.length !== 1 ? 's' : ''}.
                  {hasValidationErrors && (
                    <span className="text-red-600 ml-1">
                      Some rows have errors and will be skipped during import.
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between mt-6">
                <button onClick={() => setStep(2)} className="btn-secondary">
                  <ArrowLeft size={16} className="mr-2" />Back
                </button>
                <button onClick={executeImport} className="btn-primary" disabled={importing}>
                  {importing ? (
                    <><Loader2 size={16} className="mr-2 animate-spin" />Importing...</>
                  ) : (
                    <>Import Data<ArrowRight size={16} className="ml-2" /></>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ── Step 4: Complete ───────────────────────── */}
          {step === 4 && importResult && (
            <div className="card">
              <div className="text-center mb-6">
                <CheckCircle2 size={48} className="mx-auto mb-3 text-hunter-500" />
                <h2 className="font-display text-xl text-navy-900 mb-1">Import Complete</h2>
                <p className="text-sm text-navy-400 font-sans">
                  Successfully imported {importResult.summary?.totalImported || 0} records
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {(['entities', 'assets', 'liabilities', 'partners', 'cashFlows'] as const).map(type => {
                  const r = importResult.data?.[type];
                  if (!r) return null;
                  return (
                    <div key={type} className="p-4 bg-ivory-100 rounded-lg border border-ivory-300 text-center">
                      <p className="stat-label mb-1">{type}</p>
                      <p className="font-display text-lg text-hunter-500">{r.imported} imported</p>
                      {r.skipped > 0 && <p className="text-xs text-gold-500 mt-1">{r.skipped} skipped (duplicates)</p>}
                      {r.errors.length > 0 && <p className="text-xs text-red-500 mt-1">{r.errors.length} errors</p>}
                    </div>
                  );
                })}
              </div>
              {importResult.data && (
                <div className="space-y-2">
                  {(['entities', 'assets', 'liabilities', 'partners', 'cashFlows'] as const).map(type => {
                    const errs = importResult.data[type]?.errors || [];
                    if (errs.length === 0) return null;
                    return (
                      <div key={type} className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 font-sans">
                        <p className="font-bold mb-1">{type} errors:</p>
                        {errs.slice(0, 10).map((e: string, i: number) => <p key={i}>{e}</p>)}
                        {errs.length > 10 && <p className="italic">...and {errs.length - 10} more</p>}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex justify-center mt-6">
                <button onClick={() => { setStep(0); setSheets([]); setFileName(''); setImportResult(null); setFullData({}); }} className="btn-primary">
                  Import Another File
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Right Sidebar: Templates ──────────────────── */}
        <div>
          <div className="card">
            <h3 className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-4">
              Download Templates
            </h3>
            {[
              { key: 'master' as const, label: 'Master Template', desc: 'All sheets in one workbook', featured: true },
              { key: 'pe_bridge' as const, label: 'Osprey PE Bridge', desc: 'Format spec for PE export → Legacy import', featured: true },
              { key: 'entities' as const, label: 'Entities Only', desc: 'Entity register template' },
              { key: 'assets' as const, label: 'Assets Only', desc: 'Asset register with RE fields' },
              { key: 'liabilities' as const, label: 'Liabilities Only', desc: 'Debt schedule template' },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => generateTemplate(t.key)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border mb-2 text-left transition-colors hover:bg-ivory-100 ${
                  t.featured ? 'border-gold-300 bg-gold-50' : 'border-ivory-300'
                }`}
              >
                <div>
                  <p className="text-sm font-sans font-semibold text-navy-800">{t.label}</p>
                  <p className="text-xs text-navy-400 font-sans">{t.desc}</p>
                </div>
                <Download size={16} className="text-navy-400 flex-shrink-0" />
              </button>
            ))}
          </div>

          {/* File Info */}
          {fileName && step > 0 && step < 4 && (
            <div className="card mt-4">
              <h3 className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-3">
                Current File
              </h3>
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet size={16} className="text-navy-400" />
                <span className="text-sm font-sans text-navy-700 truncate">{fileName}</span>
              </div>
              <p className="text-xs text-navy-400 font-sans">
                {sheets.length} sheets · {totalRows} total rows · {mappedSheets.length} mapped
              </p>
              <button
                onClick={() => { setStep(0); setSheets([]); setFileName(''); setFullData({}); }}
                className="mt-3 text-xs text-red-500 font-sans hover:underline flex items-center gap-1"
              >
                <X size={12} />Clear and start over
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
